<script setup lang="ts">
import { Editor, EditorContent } from '@tiptap/vue-3'
import {
  FontFamily,
  LineHeight,
  Link,
  RequiredExtensions,
  TextAlign,
  TextBlocks,
  TextSize,
  Underline,
  Color,
} from '@gohighlevel/ghl-text-editor'
import Document from '@tiptap/extension-document'
import Gapcursor from '@tiptap/extension-gapcursor'
import Paragraph from '@tiptap/extension-paragraph'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Text from '@tiptap/extension-text'
import TextStyle from '@tiptap/extension-text-style'
import StarterKit from '@tiptap/starter-kit'
import { PropType, computed, inject, nextTick, onMounted, ref } from 'vue'
import {
  IElement,
  PositionMapping,
  PositionMarkerExtension,
  positionMarkerPluginKey,
  delay,
  CustomLineHeight,
} from '@gohighlevel/ghl-proposals-common'
import { columnResizing, tableEditing } from '@tiptap/pm/tables'
import { useBlockElements, useDocument } from '@/composable'
import { useMarkerForEditor } from '@/composable/useMarkerForEditor'
import { useFillableFieldsV2 } from '@/composable/useFillableFieldsV2'
import { useRoute } from 'vue-router'

const { documentVersion } = useDocument()
const editor = ref()
const isTableBlockFullyLoaded = ref(false)
const fillablePositionsRef = ref<PositionMapping>({})
const isPDFView = inject<boolean>('isPDFView')
const {
  getPosAtCoordsForFillables,
  insertElementsInsideEmptyMarkers,
  isPrintAdjustToConsider,
} = useMarkerForEditor()
const { getElementRefById } = useFillableFieldsV2()
const { addBlockElement } = useBlockElements()
const route = useRoute()
const debugMode = computed(() => {
  return route.query.debug === 'true'
})

onMounted(() => {
  nextTick(() => {
    addBlockElement(props.element.id, props.element.type)
  })
})

const TableCellAddAttributes: any = {
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        renderHTML: (attributes: any) => {
          if (!attributes.backgroundColor) {
            return {}
          }
          return {
            style: `background-color: ${attributes.backgroundColor}`,
          }
        },
        parseHTML: (element: any) => {
          return element.style.backgroundColor.replace(/['"]+/g, '')
        },
      },
      borderColor: {
        default: null,
        renderHTML: (attributes: any) => {
          if (!attributes.borderColor) {
            return {}
          }

          return {
            style: `border-color: ${attributes.borderColor}`,
          }
        },
        parseHTML: (element: any) => {
          return element.style.borderColor.replace(/['"]+/g, '')
        },
      },
      colwidth: {
        default: null,
        parseHTML: (element: any) => {
          const colwidth = element.getAttribute('colwidth')
          return colwidth ? colwidth.split(',').map(Number) : null
        },
      },
      width: {
        default: null,
        renderHTML: (attributes: any) => {
          if (!attributes.colwidth) {
            return {}
          }
          return {
            style: `width: ${attributes.colwidth[0]}px`,
          }
        },
        parseHTML: (element: any) => {
          return element.style.width.replace(/['"]+/g, '')
        },
      },
    }
  },
}
const props = defineProps({
  element: {
    type: Object as PropType<IElement>,
    required: true,
  },
})

const paintMarkersInPositions = async (positions: PositionMapping) => {
  if (!editor.value) return
  // Update positions through plugin
  const tr = editor.value.view.state.tr.setMeta(
    positionMarkerPluginKey,
    positions
  )
  editor.value.view.dispatch(tr)
}

editor.value = new Editor({
  extensions: [
    Document,
    Paragraph,
    Text,
    Gapcursor,
    Table.configure({
      resizable: false,
      allowTableNodeSelection: false,
    }).extend({
      addProseMirrorPlugins() {
        return [
          columnResizing({
            handleWidth: 0,
            cellMinWidth: this.options.cellMinWidth,
            View: this.options.View,
            lastColumnResizable: false,
          }),
          tableEditing({
            allowTableNodeSelection: false,
          }),
        ]
      },
    }),
    TableRow,
    TableHeader,
    TableCell.extend(TableCellAddAttributes),
    RequiredExtensions,
    TextBlocks,
    StarterKit,
    TextStyle,
    FontFamily,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Underline,
    Link,
    // LineHeight,
    ...((documentVersion.value ?? 1) > 1
      ? [
          CustomLineHeight.configure({
            defaultLineHeight: 1.5,
            types: ['paragraph', 'heading', 'blockquote', 'listItem'],
          }),
        ]
      : []),
    ...((documentVersion.value ?? 1) === 1 ? [LineHeight] : []),
    TextSize,
    Color,
    ...((documentVersion.value ?? 1) > 1 && isPDFView
      ? [
          PositionMarkerExtension.configure({
            debugMode: debugMode.value,
            getElementRefById: getElementRefById,
            isPrintAdjustToConsider: isPrintAdjustToConsider,
          }),
        ]
      : []),
  ],
  content: props.element.component.options.text,
  editable: false,
  parseOptions: {
    preserveWhitespace: 'full',
  },
  editorProps: {
    attributes: state => {
      return {
        ...state.doc.attrs,
        ...(documentVersion.value === 1
          ? { style: 'font-family: sans-serif' }
          : {}),
        ...(documentVersion.value > 1
          ? { style: 'font-family: Open Sans' }
          : {}),
      }
    },
  },
})

/**
 * Transaction is triggered when the editor is updated, there are two updates we are focusing on
 * 1. When the editor is fully loaded, we need to calculate the markers and draw them
 * 2. When the markers have been added, we need to insert the elements inside the markers
 * Do not change calculateAndDrawMarkers and handleTransactionForMarkerPlugin
 */
editor.value.on('transaction', async ({ transaction }) => {
  await handleTransactionForMarkerPlugin(transaction)
})

const calculateAndDrawMarkers = async () => {
  if (isTableBlockFullyLoaded.value) return
  await delay(500)
  const fillablePositions = getPosAtCoordsForFillables(editor, props.element)
  if (Object.keys(fillablePositions).length > 0) {
    fillablePositionsRef.value = fillablePositions
    isTableBlockFullyLoaded.value = true
    paintMarkersInPositions(fillablePositionsRef.value)
  }
}

const handleTransactionForMarkerPlugin = async (transaction: any) => {
  if (
    isPDFView &&
    (documentVersion.value ?? 1) > 1 &&
    !isPrintAdjustToConsider
  ) {
    //Block: To check if table block is fully loaded
    await calculateAndDrawMarkers()

    //Only triggered in case decorations have been created and added to the editor
    const editorView = editor.value?.view
    if (editorView) {
      const pluginMeta = transaction.getMeta(positionMarkerPluginKey)
      if (pluginMeta && isTableBlockFullyLoaded.value) {
        insertElementsInsideEmptyMarkers(
          fillablePositionsRef.value,
          debugMode.value
        )
      }
    }
  }
}

const customStyleForTextParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})

const isVersionGreaterThanOne = computed(() => {
  return documentVersion.value > 1
})
const isVersionEqualOne = computed(() => {
  return documentVersion.value === 1
})
</script>

<template>
  <div style="width: inherit">
    <div
      :style="customStyleForTextParent"
      style="overflow: auto; display: flex"
      :class="{
        'document-version-gt-1': isVersionGreaterThanOne,
        'document-version-eq-1': isVersionEqualOne,
        'pdf-view': isPDFView,
      }"
    >
      <div class="el-wrapper table-el">
        <EditorContent :editor="editor" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.document-version-gt-1 {
  .ProseMirror {
    table {
      font-weight: 400;
      text-rendering: optimizeLegibility;
      font-variant-ligatures: none;
      line-height: 1.5;
      font-size: 16px;
      word-break: break-word;
      white-space: pre-wrap;
      /* newer spec: preserves every space + breaks at them */
      white-space: break-spaces !important;
      overflow: visible !important;

      p {
        margin: 0;
        font-variant-ligatures: none;
        &[style*='text-align: justify'] {
          text-align-last: left;
        }
        &[style*='text-align: left'] {
          text-align-last: left;
        }
        &[style*='text-align: right'] {
          text-align-last: right;
        }
        &[style*='text-align: center'] {
          text-align-last: center;
        }

        span {
          /* keep your pre-wrap; then explicitly restore "break at spaces" */
          white-space: pre-wrap;
          /* newer spec: preserves every space + breaks at them */
          white-space: break-spaces !important;
          white-space-collapse: preserve; /* Chrome's way of collapsing normal spaces */
          text-wrap-mode: wrap; /* Chrome-only */
          -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
          // display: inline-block;
          display: inline;
          max-width: 100%;
          word-break: break-word;
          vertical-align: top;
        }
        a {
          display: inline;
        }
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: 0;
        span {
          /* keep your pre-wrap; then explicitly restore "break at spaces" */
          white-space: pre-wrap;
          /* newer spec: preserves every space + breaks at them */
          white-space: break-spaces !important;
          white-space-collapse: preserve; /* Chrome's way of collapsing normal spaces */
          text-wrap-mode: wrap; /* Chrome-only */
          -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
          // display: inline-block;
          max-width: 100%;
          word-break: break-word;
          display: inline;
          vertical-align: top;
        }

        a {
          display: inline;
        }
      }
    }
  }

  // .tableWrapper {
  //   .tiptap.ProseMirror[contenteditable='false'] {
  //     white-space: break-spaces !important;
  //   }
  //   p {
  //     break-inside: auto !important;
  //     font-size: 16px;
  //     line-height: 1.5;
  //   }
  // }
  // @media print {
  //   .tableWrapper {
  //     break-inside: auto;
  //     p {
  //       font-size: 16px;
  //       line-height: 1.5;
  //     }
  //   }
  // }
}

.document-version-eq-1 {
  .tiptap.ProseMirror[contenteditable='false'] {
    white-space: break-spaces !important;
    overflow: visible !important;
  }
}
</style>

<style lang="scss">
.ProseMirror {
  table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: visible !important;

    /*
    * colgroup is added by the extension method columnResizing which affects the posAtCoords method
    * so to let posAtCoords work correctly, we need to display the contents of colgroup
    */
    colgroup {
      display: contents;
    }

    td,
    th {
      min-width: 1em;
      border: 1px solid #ced4da;
      padding: 3px 5px;
      vertical-align: top;
      box-sizing: border-box;
      position: relative;
      pointer-events: none;

      > * {
        margin-bottom: 0;
      }
    }

    th {
      font-weight: bold;
      text-align: left;
      background-color: #f1f3f5;
    }

    .selectedCell:after {
      display: none;
    }

    .column-resize-handle {
      display: none;
    }

    p {
      margin: 0;
    }
  }
}
.tableWrapper {
  // padding: 1rem 0;
  overflow-x: auto;
}

.resize-cursor {
  cursor: default;
}
</style>
